import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { isAuthenticated, user } from '@stores/authStore';
import { api } from '@services/api';
import { getStatusLabel, getPriorityLabel } from '@utils/labels';
import { IssueLinkSelector } from '@components/common/IssueLinkSelector';
import { marked } from 'marked';
import { KnowledgeGraph } from '@components/organisms/KnowledgeGraph';

interface Project {
  id: number;
  code: string;
  name: string;
  description: string;
  git_repository_url?: string;
  created_at: string;
}

interface Issue {
  id: number;
  issue_number: string;
  title: string;
  status: string;
  priority: string;
  assignee_name: string;
  created_at: string;
  recent_comments_count?: number;
  is_hidden?: boolean;
}

interface User {
  id: number;
  name: string;
  email: string;
}

interface ProjectMember {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  role: string;
  joined_at: string;
}

interface Commit {
  id: number;
  commit_hash: string;
  author: string;
  message: string;
  issue_number: string | null;
  committed_at: string;
}

interface WikiPage {
  id: number;
  project_id: number;
  title: string;
  content: string;
  parent_id: number | null;
  ordering: number;
  created_by: number;
  updated_by: number | null;
  created_at: string;
  updated_at: string | null;
  linked_issues_count: number;
}

interface ProjectDetailPageProps {
  id: string;
}

export function ProjectDetailPage({ id }: ProjectDetailPageProps) {
  const [project, setProject] = useState<Project | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [allIssues, setAllIssues] = useState<Issue[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [commits, setCommits] = useState<Commit[]>([]);
  const [wikiPages, setWikiPages] = useState<WikiPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showCreateWikiModal, setShowCreateWikiModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<string>('issues');

  // Advanced filters
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterAssignee, setFilterAssignee] = useState<string>('');

  // Available users for adding as members
  const [allUsers, setAllUsers] = useState<User[]>([]);

  // Form state for creating issue
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('medium');
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [isHidden, setIsHidden] = useState(false);
  const [linkedIssues, setLinkedIssues] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<FileList | null>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<FileList | null>(null);
  const [isDraggingCreateImage, setIsDraggingCreateImage] = useState(false);
  const [isDraggingCreateFile, setIsDraggingCreateFile] = useState(false);

  // Form state for editing project
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editGitUrl, setEditGitUrl] = useState('');

  // Form state for adding member
  const [newMemberUserId, setNewMemberUserId] = useState<string>('');
  const [newMemberRole, setNewMemberRole] = useState<string>('developer');

  // Form state for wiki page
  const [wikiTitle, setWikiTitle] = useState('');
  const [wikiContent, setWikiContent] = useState('');
  const [selectedWikiPage, setSelectedWikiPage] = useState<WikiPage | null>(null);
  const [showViewWikiModal, setShowViewWikiModal] = useState(false);
  const [showEditWikiModal, setShowEditWikiModal] = useState(false);
  const [wikiLinkedIssueIds, setWikiLinkedIssueIds] = useState<number[]>([]);

  // Graph state
  const [graphNodes, setGraphNodes] = useState<any[]>([]);
  const [graphEdges, setGraphEdges] = useState<any[]>([]);
  const [graphLoading, setGraphLoading] = useState(false);

  // Drag and drop handlers for create modal images
  const handleCreateImageDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingCreateImage(true);
  };

  const handleCreateImageDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingCreateImage(false);
  };

  const handleCreateImageDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingCreateImage(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Filter for image files only
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      alert('이미지 파일만 업로드할 수 있습니다');
      return;
    }

    // Create FileList-like object
    const dt = new DataTransfer();
    imageFiles.forEach(file => dt.items.add(file));
    setSelectedImages(dt.files);
  };

  // Clipboard paste handler for create modal images
  const handleCreateImagePaste = (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length === 0) return;

    e.preventDefault();

    // Create FileList-like object
    const dt = new DataTransfer();
    imageFiles.forEach(file => dt.items.add(file));
    setSelectedImages(dt.files);
  };

  // Drag and drop handlers for create modal files
  const handleCreateFileDragOver = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingCreateFile(true);
  };

  const handleCreateFileDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingCreateFile(false);
  };

  const handleCreateFileDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDraggingCreateFile(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Create FileList-like object
    const dt = new DataTransfer();
    Array.from(files).forEach(file => dt.items.add(file));
    setSelectedAttachments(dt.files);
  };

  useEffect(() => {
    if (!isAuthenticated.value) {
      route('/login');
      return;
    }

    loadProjectData();
  }, [id]);

  // Re-apply filters when advanced filters change
  useEffect(() => {
    applyFilter(activeFilter);
  }, [filterStatus, filterPriority, filterAssignee]);

  // Load graph data when graph tab is selected
  useEffect(() => {
    if (activeTab === 'graph' && graphNodes.length === 0) {
      loadGraphData();
    }
  }, [activeTab]);

  const loadProjectData = async () => {
    setLoading(true);
    await Promise.all([loadProject(), loadIssues(), loadMembers(), loadCommits(), loadWikiPages()]);
    setLoading(false);
  };

  const loadGraphData = async () => {
    setGraphLoading(true);
    const response = await api.get<any>(`/api/projects/${id}/graph`);
    if (response.success && response.data) {
      setGraphNodes(response.data.nodes || []);
      setGraphEdges(response.data.edges || []);
    }
    setGraphLoading(false);
  };

  const loadProject = async () => {
    const response = await api.get<any>(`/api/projects/${id}`);
    if (response.success && response.data) {
      setProject(response.data);
    }
  };

  const loadIssues = async () => {
    const response = await api.get<any>(`/api/issues?project_id=${id}&limit=1000`);
    if (response.success && response.data) {
      const issuesList = response.data.items || [];
      setAllIssues(issuesList);
      applyFilter('all', issuesList);
    }
  };

  const applyFilter = (filter: string, issuesList?: Issue[]) => {
    const source = issuesList || allIssues;
    setActiveFilter(filter);

    let filtered = source;

    switch (filter) {
      case 'all':
        filtered = source;
        break;
      case 'on_hold':
        filtered = source.filter((issue) => issue.status === 'on_hold');
        break;
      case 'review':
        filtered = source.filter((issue) => issue.status === 'review');
        break;
      case 'in_progress':
        filtered = source.filter((issue) => issue.status === 'in_progress');
        break;
      case 'resolved':
        filtered = source.filter((issue) => issue.status === 'resolved');
        break;
      case 'updated':
        filtered = source.filter((issue) => issue.status === 'updated');
        break;
      default:
        filtered = source;
    }

    // Apply advanced filters
    filtered = applyAdvancedFilters(filtered);
    setIssues(filtered);
  };

  const applyAdvancedFilters = (issuesList: Issue[]) => {
    let filtered = issuesList;

    // Filter by status
    if (filterStatus) {
      filtered = filtered.filter((issue) => issue.status === filterStatus);
    }

    // Filter by priority
    if (filterPriority) {
      filtered = filtered.filter((issue) => issue.priority === filterPriority);
    }

    // Filter by assignee
    if (filterAssignee) {
      if (filterAssignee === 'unassigned') {
        filtered = filtered.filter((issue) => !issue.assignee_name);
      } else {
        filtered = filtered.filter((issue) => issue.assignee_name === filterAssignee);
      }
    }

    return filtered;
  };

  const resetAdvancedFilters = () => {
    setFilterStatus('');
    setFilterPriority('');
    setFilterAssignee('');
    applyFilter(activeFilter);
  };

  const getIssueStats = () => {
    return {
      total: allIssues.length,
      onHold: allIssues.filter((issue) => issue.status === 'on_hold').length,
      review: allIssues.filter((issue) => issue.status === 'review').length,
      inProgress: allIssues.filter((issue) => issue.status === 'in_progress').length,
      resolved: allIssues.filter((issue) => issue.status === 'resolved').length,
      updated: allIssues.filter((issue) => issue.status === 'updated').length,
    };
  };

  const loadMembers = async () => {
    const response = await api.get<any>(`/api/projects/${id}`);
    if (response.success && response.data) {
      setMembers(response.data.members || []);
    }
  };

  const loadAllUsers = async () => {
    const response = await api.get<any>('/api/users');
    if (response.success && response.data) {
      setAllUsers(response.data.items || []);
    }
  };

  const loadCommits = async () => {
    const response = await api.get<any>(`/api/commits?project_id=${id}&limit=10`);
    if (response.success && response.data) {
      setCommits(response.data.items || []);
    }
  };

  const loadWikiPages = async () => {
    if (!canAccessWiki()) return;

    const response = await api.get<any>(`/api/projects/${id}/wiki`);
    if (response.success && response.data) {
      setWikiPages(response.data || []);
    }
  };

  const handleCreateWikiPage = async (e: Event) => {
    e.preventDefault();

    if (!wikiTitle.trim()) {
      alert('제목을 입력하세요');
      return;
    }

    const payload = {
      title: wikiTitle.trim(),
      content: wikiContent.trim(),
      ordering: 0,
      linked_issue_ids: wikiLinkedIssueIds,
    };

    const response = await api.post<any>(`/api/projects/${id}/wiki`, payload);
    if (response.success) {
      setShowCreateWikiModal(false);
      setWikiTitle('');
      setWikiContent('');
      setWikiLinkedIssueIds([]);
      loadWikiPages();
    } else {
      alert(response.error || '문서 생성에 실패했습니다');
    }
  };

  const handleWikiPageClick = async (page: WikiPage) => {
    // Fetch full wiki page details including linked issues
    const response = await api.get<any>(`/api/projects/${id}/wiki/${page.id}`);
    if (response.success && response.data) {
      const fullPage = response.data;
      setSelectedWikiPage(page);
      setWikiTitle(fullPage.title);
      setWikiContent(fullPage.content);

      // Extract linked issue IDs
      const linkedIds = fullPage.linked_issues?.map((issue: any) => issue.id) || [];
      setWikiLinkedIssueIds(linkedIds);

      setShowViewWikiModal(true);
    } else {
      alert(response.error || '문서 로드에 실패했습니다');
    }
  };

  const handleEditWikiPage = async (e: Event) => {
    e.preventDefault();

    if (!selectedWikiPage || !wikiTitle.trim()) {
      alert('제목을 입력하세요');
      return;
    }

    const payload = {
      title: wikiTitle.trim(),
      content: wikiContent.trim(),
      linked_issue_ids: wikiLinkedIssueIds,
    };

    const response = await api.put<any>(`/api/projects/${id}/wiki/${selectedWikiPage.id}`, payload);
    if (response.success) {
      setShowEditWikiModal(false);
      setSelectedWikiPage(null);
      setWikiTitle('');
      setWikiContent('');
      setWikiLinkedIssueIds([]);
      loadWikiPages();
    } else {
      alert(response.error || '문서 수정에 실패했습니다');
    }
  };

  const handleDeleteWikiPage = async () => {
    if (!selectedWikiPage) return;

    if (!confirm(`'${selectedWikiPage.title}' 문서를 삭제하시겠습니까?`)) {
      return;
    }

    const response = await api.delete<any>(`/api/projects/${id}/wiki/${selectedWikiPage.id}`);
    if (response.success) {
      setShowEditWikiModal(false);
      setSelectedWikiPage(null);
      setWikiTitle('');
      setWikiContent('');
      setWikiLinkedIssueIds([]);
      loadWikiPages();
    } else {
      alert(response.error || '문서 삭제에 실패했습니다');
    }
  };

  const handleCreateIssue = async (e: Event) => {
    e.preventDefault();

    if (!title.trim()) {
      alert('Please fill in required fields');
      return;
    }

    const payload: any = {
      project_id: parseInt(id),
      title: title.trim(),
      description: description.trim(),
      priority,
      assignee_id: assigneeId ? parseInt(assigneeId) : null,
      is_hidden: isHidden,
    };

    // Process linked issues
    if (linkedIssues.length > 0) {
      payload.linked_issue_numbers = linkedIssues;
    }

    const response = await api.post<any>('/api/issues', payload);
    if (response.success) {
      const issueId = response.data.id;

      // Upload images if selected
      if (selectedImages && selectedImages.length > 0) {
        for (let i = 0; i < selectedImages.length; i++) {
          await api.uploadFile(`/api/issues/${issueId}/images`, selectedImages[i]);
        }
      }

      // Upload attachments if selected
      if (selectedAttachments && selectedAttachments.length > 0) {
        for (let i = 0; i < selectedAttachments.length; i++) {
          await api.uploadFile(`/api/issues/${issueId}/attachments`, selectedAttachments[i]);
        }
      }

      setShowCreateModal(false);
      setTitle('');
      setDescription('');
      setPriority('medium');
      setAssigneeId('');
      setIsHidden(false);
      setLinkedIssues([]);
      setSelectedImages(null);
      setSelectedAttachments(null);
      loadIssues();
    } else {
      alert(response.error || 'Failed to create issue');
    }
  };

  const handleEditProject = async (e: Event) => {
    e.preventDefault();

    if (!editName.trim()) {
      alert('Please enter a project name');
      return;
    }

    const payload: any = {
      name: editName.trim(),
      description: editDescription.trim(),
      git_repository_url: editGitUrl.trim() || null,
    };

    const response = await api.put<any>(`/api/projects/${id}`, payload);
    if (response.success) {
      setShowEditModal(false);
      loadProject();
    } else {
      alert(response.error || 'Failed to update project');
    }
  };

  const handleAddMember = async (e: Event) => {
    e.preventDefault();

    if (!newMemberUserId) {
      alert('Please select a user');
      return;
    }

    const payload = {
      user_id: parseInt(newMemberUserId),
      role: newMemberRole,
    };

    const response = await api.post<any>(`/api/projects/${id}/members`, payload);
    if (response.success) {
      setShowAddMemberModal(false);
      setNewMemberUserId('');
      setNewMemberRole('developer');
      loadMembers();
    } else {
      alert(response.error || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (memberId: number, memberName: string) => {
    if (!confirm(`Remove ${memberName} from this project?`)) {
      return;
    }

    const response = await api.delete<any>(`/api/projects/${id}/members/${memberId}`);
    if (response.success) {
      loadMembers();
    } else {
      alert(response.error || 'Failed to remove member');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      review: 'bg-purple-100 text-purple-800',
      on_hold: 'bg-orange-100 text-orange-800',
      rejected: 'bg-red-600 text-white font-semibold',
      resolved: 'bg-green-100 text-green-800',
      updated: 'bg-cyan-100 text-cyan-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      critical: 'bg-red-100 text-red-600',
    };
    return colors[priority] || 'bg-gray-100 text-gray-600';
  };

  const getCurrentUserRole = (): string | null => {
    if (user.value?.is_admin) return 'admin';
    const member = members.find(m => m.user_id === user.value?.id);
    return member?.role || null;
  };

  const canAccessWiki = (): boolean => {
    const role = getCurrentUserRole();
    return role !== 'qa' && role !== null;
  };

  if (!isAuthenticated.value) return null;

  return (
    <div
      class="min-h-screen bg-gray-100"
      onDragOver={(e: DragEvent) => e.preventDefault()}
      onDrop={(e: DragEvent) => e.preventDefault()}
    >
      <header class="bg-white shadow">
        <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div>
            <button onClick={() => route('/dashboard')} class="mb-2 text-sm text-blue-600">
              ← 대시보드로 돌아가기
            </button>
            {project && (
              <div>
                <h1 class="text-xl font-bold">
                  <span class="font-mono text-blue-600">{project.code}</span> - {project.name}
                </h1>
                {project.description && (
                  <p class="text-sm text-gray-600">{project.description}</p>
                )}
              </div>
            )}
          </div>
          <div class="flex gap-2">
            {user.value?.is_admin && (
              <button
                onClick={() => {
                  setEditName(project?.name || '');
                  setEditDescription(project?.description || '');
                  setEditGitUrl(project?.git_repository_url || '');
                  setShowEditModal(true);
                }}
                class="btn btn-secondary"
              >
                프로젝트 수정
              </button>
            )}
            <button onClick={() => setShowCreateModal(true)} class="btn btn-primary">
              이슈 생성
            </button>
          </div>
        </div>
      </header>

      <main class="mx-auto max-w-7xl px-4 py-8">
        {loading ? (
          <div class="text-center">로딩중...</div>
        ) : (
          <div>
            {/* Tab Navigation */}
            <div class="mb-6 border-b border-gray-200">
              <nav class="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('overview')}
                  class={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
                    activeTab === 'overview'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  개요
                </button>
                <button
                  onClick={() => setActiveTab('issues')}
                  class={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
                    activeTab === 'issues'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  이슈
                </button>
                {canAccessWiki() && (
                  <button
                    onClick={() => setActiveTab('wiki')}
                    class={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
                      activeTab === 'wiki'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    문서
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('graph')}
                  class={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${
                    activeTab === 'graph'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  그래프
                </button>
              </nav>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <div class="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              <button
                onClick={() => applyFilter('all')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'all' ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">전체 이슈</h3>
                <p class="text-3xl font-bold text-blue-600">{getIssueStats().total}</p>
              </button>
              <button
                onClick={() => applyFilter('on_hold')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'on_hold' ? 'ring-2 ring-gray-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">보류된 이슈</h3>
                <p class="text-3xl font-bold text-gray-500">{getIssueStats().onHold}</p>
              </button>
              <button
                onClick={() => applyFilter('review')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'review' ? 'ring-2 ring-purple-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">검토중 이슈</h3>
                <p class="text-3xl font-bold text-purple-600">{getIssueStats().review}</p>
              </button>
              <button
                onClick={() => applyFilter('in_progress')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'in_progress' ? 'ring-2 ring-yellow-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">진행중 이슈</h3>
                <p class="text-3xl font-bold text-yellow-600">{getIssueStats().inProgress}</p>
              </button>
              <button
                onClick={() => applyFilter('resolved')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'resolved' ? 'ring-2 ring-green-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">해결된 이슈</h3>
                <p class="text-3xl font-bold text-green-600">{getIssueStats().resolved}</p>
              </button>
              <button
                onClick={() => applyFilter('updated')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'updated' ? 'ring-2 ring-cyan-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">업데이트된 이슈</h3>
                <p class="text-3xl font-bold text-cyan-600">{getIssueStats().updated}</p>
              </button>
            </div>

            {/* Team Members */}
            <div class="card mt-6">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-lg font-semibold">Team Members ({members.length})</h3>
                {user.value?.is_admin && (
                  <button
                    onClick={() => {
                      loadAllUsers();
                      setShowAddMemberModal(true);
                    }}
                    class="btn btn-primary"
                  >
                    Add Member
                  </button>
                )}
              </div>
              <div class="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.id}
                    class="flex items-center justify-between border-b py-3 last:border-b-0"
                  >
                    <div class="flex-1">
                      <p class="font-medium">{member.user_name}</p>
                      <p class="text-sm text-gray-600">{member.user_email}</p>
                    </div>
                    <div class="flex items-center gap-3">
                      <span
                        class={`rounded px-2 py-1 text-xs ${
                          member.role === 'pm'
                            ? 'bg-purple-100 text-purple-800'
                            : member.role === 'qa'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {member.role.toUpperCase()}
                      </span>
                      {user.value?.is_admin && (
                        <button
                          onClick={() => handleRemoveMember(member.id, member.user_name)}
                          class="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {members.length === 0 && (
                  <p class="py-4 text-center text-gray-500">No members yet</p>
                )}
              </div>
            </div>

            {/* Recent Commits */}
            <div class="card mt-6">
              <h3 class="mb-4 text-lg font-semibold">Recent Commits</h3>
              {commits.length > 0 ? (
                <div class="space-y-3">
                  {commits.map((commit) => (
                    <div key={commit.id} class="border-b pb-3 last:border-b-0">
                      <div class="flex items-start justify-between">
                        <div class="flex-1">
                          <p class="text-sm font-medium text-gray-900">{commit.message}</p>
                          <div class="mt-1 flex items-center gap-3 text-xs text-gray-500">
                            <span class="font-mono">{commit.commit_hash.substring(0, 8)}</span>
                            <span>{commit.author}</span>
                            {commit.issue_number && (
                              <span class="rounded bg-blue-100 px-2 py-0.5 font-mono text-blue-800">
                                {commit.issue_number}
                              </span>
                            )}
                          </div>
                        </div>
                        <span class="text-xs text-gray-500">
                          {new Date(commit.committed_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p class="text-center text-gray-500 py-4">No commits tracked yet</p>
              )}
            </div>
              </div>
            )}

            {/* Issues Tab */}
            {activeTab === 'issues' && (
              <div>
            <div class="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              <button
                onClick={() => applyFilter('all')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'all' ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">전체 이슈</h3>
                <p class="text-3xl font-bold text-blue-600">{getIssueStats().total}</p>
              </button>
              <button
                onClick={() => applyFilter('on_hold')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'on_hold' ? 'ring-2 ring-gray-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">보류된 이슈</h3>
                <p class="text-3xl font-bold text-gray-500">{getIssueStats().onHold}</p>
              </button>
              <button
                onClick={() => applyFilter('review')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'review' ? 'ring-2 ring-purple-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">검토중 이슈</h3>
                <p class="text-3xl font-bold text-purple-600">{getIssueStats().review}</p>
              </button>
              <button
                onClick={() => applyFilter('in_progress')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'in_progress' ? 'ring-2 ring-yellow-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">진행중 이슈</h3>
                <p class="text-3xl font-bold text-yellow-600">{getIssueStats().inProgress}</p>
              </button>
              <button
                onClick={() => applyFilter('resolved')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'resolved' ? 'ring-2 ring-green-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">해결된 이슈</h3>
                <p class="text-3xl font-bold text-green-600">{getIssueStats().resolved}</p>
              </button>
              <button
                onClick={() => applyFilter('updated')}
                class={`card text-left cursor-pointer transition-all hover:shadow-md ${
                  activeFilter === 'updated' ? 'ring-2 ring-cyan-500' : ''
                }`}
              >
                <h3 class="mb-2 text-sm text-gray-600">업데이트된 이슈</h3>
                <p class="text-3xl font-bold text-cyan-600">{getIssueStats().updated}</p>
              </button>
            </div>

            <div class="card">
              <div class="mb-4 flex items-center justify-between">
                <h3 class="text-lg font-semibold">이슈 목록 ({issues.length})</h3>
                {(activeFilter !== 'all' || filterStatus || filterPriority || filterAssignee) && (
                  <button
                    onClick={() => {
                      setActiveFilter('all');
                      resetAdvancedFilters();
                    }}
                    class="text-sm text-blue-600 hover:text-blue-800"
                  >
                    모든 필터 초기화
                  </button>
                )}
              </div>

              {/* Advanced Filters */}
              <div class="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3 border-b pb-4">
                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">상태</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus((e.target as HTMLSelectElement).value)}
                    class="input w-full text-sm"
                  >
                    <option value="">전체</option>
                    <option value="rejected">거절됨</option>
                    <option value="new">신규</option>
                    <option value="review">검토중</option>
                    <option value="in_progress">진행중</option>
                    <option value="on_hold">보류됨</option>
                    <option value="resolved">해결됨</option>
                    <option value="updated">업데이트완료</option>
                  </select>
                </div>

                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">우선순위</label>
                  <select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority((e.target as HTMLSelectElement).value)}
                    class="input w-full text-sm"
                  >
                    <option value="">전체</option>
                    <option value="low">낮음</option>
                    <option value="medium">보통</option>
                    <option value="high">높음</option>
                    <option value="critical">긴급</option>
                  </select>
                </div>

                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-1">담당자</label>
                  <select
                    value={filterAssignee}
                    onChange={(e) => setFilterAssignee((e.target as HTMLSelectElement).value)}
                    class="input w-full text-sm"
                  >
                    <option value="">전체</option>
                    <option value="unassigned">미할당</option>
                    {Array.from(new Set(allIssues.map(issue => issue.assignee_name).filter(Boolean))).map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <table class="w-full">
                <thead>
                  <tr class="border-b">
                    <th class="py-3 text-left">이슈 번호</th>
                    <th class="py-3 text-left">제목</th>
                    <th class="py-3 text-left">상태</th>
                    <th class="py-3 text-left">우선순위</th>
                    <th class="py-3 text-left">담당자</th>
                    <th class="py-3 text-left">생성일</th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((issue) => (
                    <tr
                      key={issue.id}
                      onClick={() => route(`/issues/${issue.id}`)}
                      class="cursor-pointer border-b hover:bg-gray-50"
                    >
                      <td class="py-3">
                        <span class="font-mono text-sm">{issue.issue_number}</span>
                      </td>
                      <td class="py-3">
                        {issue.is_hidden && (
                          <svg class="inline-block w-4 h-4 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                          </svg>
                        )}
                        {issue.title}
                        {(issue.recent_comments_count ?? 0) > 0 && (
                          <span class="ml-2 inline-flex items-center gap-1 rounded bg-blue-500 px-2 py-1 text-xs font-semibold text-white">
                            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z"/>
                            </svg>
                            {issue.recent_comments_count}
                          </span>
                        )}
                      </td>
                      <td class="py-3">
                        <span class={`rounded px-2 py-1 text-xs ${getStatusBadge(issue.status)}`}>
                          {getStatusLabel(issue.status)}
                        </span>
                      </td>
                      <td class="py-3">
                        <span
                          class={`rounded px-2 py-1 text-xs ${getPriorityBadge(issue.priority)}`}
                        >
                          {getPriorityLabel(issue.priority)}
                        </span>
                      </td>
                      <td class="py-3 text-sm">{issue.assignee_name || '미할당'}</td>
                      <td class="py-3 text-sm text-gray-600">
                        {new Date(issue.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {issues.length === 0 && (
                <div class="py-8 text-center text-gray-500">No issues yet</div>
              )}
            </div>
              </div>
            )}

            {/* Wiki Tab */}
            {activeTab === 'wiki' && canAccessWiki() && (
              <div>
                <div class="card">
                  <div class="mb-4 flex items-center justify-between">
                    <h3 class="text-lg font-semibold">문서 관리</h3>
                    <button
                      onClick={() => setShowCreateWikiModal(true)}
                      class="btn btn-primary"
                    >
                      + 새 문서
                    </button>
                  </div>

                  {wikiPages.length > 0 ? (
                    <div class="space-y-2">
                      {wikiPages.map((page) => (
                        <div
                          key={page.id}
                          onClick={() => handleWikiPageClick(page)}
                          class="border-b py-4 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                        >
                          <div class="flex items-start justify-between">
                            <div class="flex-1">
                              <h4 class="font-medium text-gray-900">{page.title}</h4>
                              {page.content && (
                                <p class="mt-1 text-sm text-gray-600 line-clamp-2">
                                  {page.content.substring(0, 150)}
                                  {page.content.length > 150 && '...'}
                                </p>
                              )}
                              <div class="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                <span>
                                  생성: {new Date(page.created_at).toLocaleDateString()}
                                </span>
                                {page.updated_at && (
                                  <span>
                                    수정: {new Date(page.updated_at).toLocaleDateString()}
                                  </span>
                                )}
                                {page.linked_issues_count > 0 && (
                                  <span class="rounded bg-blue-100 px-2 py-0.5 text-blue-800">
                                    연결된 이슈: {page.linked_issues_count}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div class="text-center py-12 text-gray-500">
                      <p class="mb-2">아직 문서가 없습니다</p>
                      <p class="text-sm">프로젝트 문서를 작성하여 팀원들과 공유하세요</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Graph Tab */}
            {activeTab === 'graph' && (
              <div class="card">
                <div class="mb-4 flex items-center justify-between">
                  <h3 class="text-lg font-semibold">지식 그래프</h3>
                  <button
                    onClick={loadGraphData}
                    class="btn btn-secondary text-sm"
                  >
                    새로고침
                  </button>
                </div>
                {graphLoading ? (
                  <div class="py-16 text-center text-gray-500">그래프 데이터 로딩중...</div>
                ) : graphNodes.length === 0 ? (
                  <div class="py-16 text-center text-gray-500">
                    <p class="mb-2">그래프 데이터가 없습니다</p>
                    <p class="text-sm">이슈, 커밋, 위키를 추가하면 관계 그래프가 표시됩니다</p>
                  </div>
                ) : (
                  <KnowledgeGraph nodes={graphNodes} edges={graphEdges} />
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create Issue Modal */}
      {showCreateModal && (
        <div
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onDragOver={(e: DragEvent) => e.preventDefault()}
          onDrop={(e: DragEvent) => e.preventDefault()}
        >
          <div class="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 class="mb-4 text-xl font-bold">
              {project?.code} 새 이슈 생성
            </h2>
            <form onSubmit={handleCreateIssue}>
              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">제목 *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  required
                />
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">설명</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
                  class="input w-full"
                  rows={4}
                />
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">우선순위</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority((e.target as HTMLSelectElement).value)}
                  class="input w-full"
                >
                  <option value="low">낮음</option>
                  <option value="medium">보통</option>
                  <option value="high">높음</option>
                  <option value="critical">긴급</option>
                </select>
              </div>

              {(() => {
                const currentUserRole = members.find((m) => m.user_id === user.value?.id)?.role;
                return currentUserRole !== 'qa' ? (
                  <div class="mb-4">
                    <label class="mb-1 block text-sm font-medium">담당자</label>
                    <select
                      value={assigneeId}
                      onChange={(e) => setAssigneeId((e.target as HTMLSelectElement).value)}
                      class="input w-full"
                    >
                      <option value="">미할당</option>
                      {members.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.user_name} ({member.user_email})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null;
              })()}

              <div class="mb-4">
                <IssueLinkSelector
                  projectId={parseInt(id)}
                  selectedIssues={linkedIssues}
                  onChange={setLinkedIssues}
                />
              </div>

              {(() => {
                const currentUserRole = members.find((m) => m.user_id === user.value?.id)?.role;
                return currentUserRole !== 'qa' ? (
                  <div class="mb-4">
                    <label class="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isHidden}
                        onChange={(e) => setIsHidden((e.target as HTMLInputElement).checked)}
                        class="rounded"
                      />
                      <span>🔒 히든 이슈 (QA에게 숨김)</span>
                    </label>
                  </div>
                ) : null;
              })()}

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">이미지</label>

                {/* Image Drag and Drop Zone */}
                <div
                  tabIndex={0}
                  onDragOver={handleCreateImageDragOver}
                  onDragLeave={handleCreateImageDragLeave}
                  onDrop={handleCreateImageDrop}
                  onPaste={handleCreateImagePaste as any}
                  class={`mb-2 border-2 border-dashed rounded-lg text-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDraggingCreateImage
                      ? 'border-blue-600 bg-blue-100 p-12 scale-105 shadow-lg'
                      : 'border-gray-300 bg-gray-50 hover:border-gray-400 p-6'
                  }`}
                >
                  {isDraggingCreateImage ? (
                    <div class="animate-pulse">
                      <div class="text-5xl mb-3">📥</div>
                      <p class="text-base font-semibold text-blue-700">
                        여기에 이미지를 놓으세요
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p class="text-sm text-gray-600">
                        📷 이미지를 드래그하여 놓거나, 클릭 후 Ctrl+V로 붙여넣으세요
                      </p>
                      <p class="text-xs text-gray-400 mt-1">
                        {selectedImages && selectedImages.length > 0
                          ? `${selectedImages.length}개 선택됨`
                          : '여러 이미지를 선택할 수 있습니다'}
                      </p>
                    </div>
                  )}
                </div>

                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setSelectedImages((e.target as HTMLInputElement).files)}
                  class="input w-full"
                />
              </div>

              <div class="mb-6">
                <label class="mb-1 block text-sm font-medium">파일 첨부</label>

                {/* File Drag and Drop Zone */}
                <div
                  tabIndex={0}
                  onDragOver={handleCreateFileDragOver}
                  onDragLeave={handleCreateFileDragLeave}
                  onDrop={handleCreateFileDrop}
                  class={`mb-2 border-2 border-dashed rounded-lg text-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    isDraggingCreateFile
                      ? 'border-green-600 bg-green-100 p-12 scale-105 shadow-lg'
                      : 'border-gray-300 bg-gray-50 hover:border-gray-400 p-6'
                  }`}
                >
                  {isDraggingCreateFile ? (
                    <div class="animate-pulse">
                      <div class="text-5xl mb-3">📂</div>
                      <p class="text-base font-semibold text-green-700">
                        여기에 파일을 놓으세요
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p class="text-sm text-gray-600">
                        📎 파일을 드래그하여 놓으세요
                      </p>
                      <p class="text-xs text-gray-400 mt-1">
                        {selectedAttachments && selectedAttachments.length > 0
                          ? `${selectedAttachments.length}개 선택됨`
                          : 'Excel, Text, Markdown, ZIP'}
                      </p>
                    </div>
                  )}
                </div>

                <input
                  type="file"
                  accept=".xls,.xlsx,.txt,.md,.zip"
                  multiple
                  onChange={(e) => setSelectedAttachments((e.target as HTMLInputElement).files)}
                  class="input w-full"
                />
              </div>

              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-primary">
                  이슈 생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div class="card w-full max-w-lg">
            <h2 class="mb-4 text-xl font-bold">프로젝트 수정: {project?.code}</h2>
            <form onSubmit={handleEditProject}>
              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">프로젝트 이름 *</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  required
                />
              </div>

              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">설명</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription((e.target as HTMLTextAreaElement).value)}
                  class="input w-full"
                  rows={3}
                  placeholder="프로젝트에 대한 간단한 설명"
                />
              </div>

              <div class="mb-6">
                <label class="mb-1 block text-sm font-medium">Git 저장소 URL</label>
                <input
                  type="url"
                  value={editGitUrl}
                  onChange={(e) => setEditGitUrl((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  placeholder="예: https://github.com/username/repository.git"
                />
                <p class="mt-1 text-xs text-gray-500">
                  선택사항. Git 연동 및 커밋 추적에 필요
                </p>
              </div>

              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-primary">
                  프로젝트 수정
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div class="card w-full max-w-lg">
            <h2 class="mb-4 text-xl font-bold">{project?.code} 멤버 추가</h2>
            <form onSubmit={handleAddMember}>
              <div class="mb-4">
                <label class="mb-1 block text-sm font-medium">사용자 *</label>
                <select
                  value={newMemberUserId}
                  onChange={(e) => setNewMemberUserId((e.target as HTMLSelectElement).value)}
                  class="input w-full"
                  required
                >
                  <option value="">사용자 선택</option>
                  {allUsers
                    .filter((u) => !members.some((m) => m.user_id === u.id))
                    .map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                </select>
                <p class="mt-1 text-xs text-gray-500">
                  프로젝트에 이미 속한 사용자는 표시되지 않습니다
                </p>
              </div>

              <div class="mb-6">
                <label class="mb-1 block text-sm font-medium">역할 *</label>
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole((e.target as HTMLSelectElement).value)}
                  class="input w-full"
                  required
                >
                  <option value="developer">개발자</option>
                  <option value="pm">PM (프로젝트 관리자)</option>
                  <option value="qa">QA (품질 보증)</option>
                </select>
                <p class="mt-1 text-xs text-gray-500">
                  PM은 프로젝트 멤버를 관리하고 이슈를 승인할 수 있습니다
                </p>
              </div>

              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddMemberModal(false)}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-primary">
                  멤버 추가
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Wiki Page Modal */}
      {showCreateWikiModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div class="card w-full max-w-2xl">
            <h2 class="mb-4 text-xl font-bold">새 문서 생성</h2>
            <form onSubmit={handleCreateWikiPage}>
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700">
                  제목 <span class="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={wikiTitle}
                  onInput={(e) => setWikiTitle((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  placeholder="문서 제목을 입력하세요"
                  required
                />
              </div>
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700">
                  내용
                </label>
                <textarea
                  value={wikiContent}
                  onInput={(e) => setWikiContent((e.target as HTMLTextAreaElement).value)}
                  class="input w-full"
                  rows={15}
                  placeholder="문서 내용을 입력하세요 (마크다운 지원 예정)"
                />
              </div>
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700">
                  연결된 이슈
                </label>
                <div class="max-h-40 overflow-y-auto rounded border border-gray-300 bg-gray-50 p-2">
                  {issues.length > 0 ? (
                    <div class="space-y-1">
                      {issues.map((issue) => (
                        <label key={issue.id} class="flex items-center space-x-2 cursor-pointer p-1 hover:bg-white rounded">
                          <input
                            type="checkbox"
                            checked={wikiLinkedIssueIds.includes(issue.id)}
                            onChange={(e) => {
                              const checked = (e.target as HTMLInputElement).checked;
                              if (checked) {
                                setWikiLinkedIssueIds([...wikiLinkedIssueIds, issue.id]);
                              } else {
                                setWikiLinkedIssueIds(wikiLinkedIssueIds.filter(id => id !== issue.id));
                              }
                            }}
                            class="rounded"
                          />
                          <span class="text-sm">
                            #{issue.issue_number} - {issue.title}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p class="text-sm text-gray-500 text-center py-2">이슈가 없습니다</p>
                  )}
                </div>
              </div>
              <div class="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateWikiModal(false);
                    setWikiTitle('');
                    setWikiContent('');
                    setWikiLinkedIssueIds([]);
                  }}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-primary">
                  생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Wiki Page Modal */}
      {showViewWikiModal && selectedWikiPage && (
        <div
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => {
            setShowViewWikiModal(false);
            setSelectedWikiPage(null);
            setWikiTitle('');
            setWikiContent('');
            setWikiLinkedIssueIds([]);
          }}
        >
          <div
            class="card w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div class="mb-4 flex items-center justify-between">
              <h2 class="text-xl font-bold">{wikiTitle}</h2>
              <div class="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewWikiModal(false);
                    setShowEditWikiModal(true);
                  }}
                  class="btn btn-secondary text-sm"
                >
                  수정
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowViewWikiModal(false);
                    setSelectedWikiPage(null);
                    setWikiTitle('');
                    setWikiContent('');
                    setWikiLinkedIssueIds([]);
                  }}
                  class="btn btn-ghost text-sm"
                >
                  닫기
                </button>
              </div>
            </div>

            {/* Markdown content */}
            <div
              class="prose prose-sm max-w-none mb-6"
              dangerouslySetInnerHTML={{ __html: String(marked.parse(wikiContent)) }}
            />

            {/* Linked issues */}
            {wikiLinkedIssueIds.length > 0 && (
              <div class="border-t pt-4">
                <h3 class="text-sm font-semibold mb-3">연결된 이슈</h3>
                <div class="space-y-2">
                  {issues
                    .filter(issue => wikiLinkedIssueIds.includes(issue.id))
                    .map(issue => (
                      <div
                        key={issue.id}
                        class="flex items-center gap-2 text-sm p-2 rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setShowViewWikiModal(false);
                          route(`/issues/${issue.id}`);
                        }}
                      >
                        <span class="font-mono text-gray-500">#{issue.issue_number}</span>
                        <span class={`px-2 py-0.5 rounded text-xs ${
                          issue.priority === 'critical' ? 'bg-red-100 text-red-800' :
                          issue.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          issue.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {getPriorityLabel(issue.priority)}
                        </span>
                        <span class="flex-1">{issue.title}</span>
                        <span class={`px-2 py-0.5 rounded text-xs ${
                          issue.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                          issue.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          issue.status === 'resolved' ? 'bg-green-100 text-green-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {getStatusLabel(issue.status)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Wiki Page Modal */}
      {showEditWikiModal && selectedWikiPage && (
        <div
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => {
            setShowEditWikiModal(false);
            setSelectedWikiPage(null);
            setWikiTitle('');
            setWikiContent('');
            setWikiLinkedIssueIds([]);
          }}
        >
          <div
            class="card w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div class="mb-4 flex items-center justify-between">
              <h2 class="text-xl font-bold">문서 수정</h2>
              <button
                type="button"
                onClick={handleDeleteWikiPage}
                class="btn btn-danger text-sm"
              >
                삭제
              </button>
            </div>
            <form onSubmit={handleEditWikiPage}>
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700">
                  제목 <span class="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={wikiTitle}
                  onInput={(e) => setWikiTitle((e.target as HTMLInputElement).value)}
                  class="input w-full"
                  placeholder="문서 제목을 입력하세요"
                  required
                />
              </div>
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700">
                  내용
                </label>
                <textarea
                  value={wikiContent}
                  onInput={(e) => setWikiContent((e.target as HTMLTextAreaElement).value)}
                  class="input w-full"
                  rows={15}
                  placeholder="문서 내용을 입력하세요 (마크다운 지원 예정)"
                />
              </div>
              <div class="mb-4">
                <label class="mb-2 block text-sm font-medium text-gray-700">
                  연결된 이슈
                </label>
                <div class="max-h-40 overflow-y-auto rounded border border-gray-300 bg-gray-50 p-2">
                  {issues.length > 0 ? (
                    <div class="space-y-1">
                      {issues.map((issue) => (
                        <label key={issue.id} class="flex items-center space-x-2 cursor-pointer p-1 hover:bg-white rounded">
                          <input
                            type="checkbox"
                            checked={wikiLinkedIssueIds.includes(issue.id)}
                            onChange={(e) => {
                              const checked = (e.target as HTMLInputElement).checked;
                              if (checked) {
                                setWikiLinkedIssueIds([...wikiLinkedIssueIds, issue.id]);
                              } else {
                                setWikiLinkedIssueIds(wikiLinkedIssueIds.filter(id => id !== issue.id));
                              }
                            }}
                            class="rounded"
                          />
                          <span class="text-sm">
                            #{issue.issue_number} - {issue.title}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p class="text-sm text-gray-500 text-center py-2">이슈가 없습니다</p>
                  )}
                </div>
              </div>
              <div class="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditWikiModal(false);
                    setSelectedWikiPage(null);
                    setWikiTitle('');
                    setWikiContent('');
                    setWikiLinkedIssueIds([]);
                  }}
                  class="btn btn-secondary"
                >
                  취소
                </button>
                <button type="submit" class="btn btn-primary">
                  저장
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
